/** Formatierungshelfer (de-DE). */

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return '–';
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return '–';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatTime(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return '–';
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/** "vor 3 Std." / "in 45 Min." */
export function formatRelative(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return '–';
  const diffMinutes = Math.round((date.getTime() - Date.now()) / 60_000);
  const absolute = Math.abs(diffMinutes);
  const future = diffMinutes > 0;

  const render = (amount: number, unit: string) =>
    future ? `in ${amount} ${unit}` : `vor ${amount} ${unit}`;

  if (absolute < 1) return 'gerade eben';
  if (absolute < 60) return render(absolute, 'Min.');
  if (absolute < 1440) return render(Math.round(absolute / 60), 'Std.');
  if (absolute < 43200) return render(Math.round(absolute / 1440), 'Tg.');
  return formatDate(value);
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null) return '–';
  const negative = minutes < 0;
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  const text = hours > 0 ? `${hours} h ${String(rest).padStart(2, '0')} min` : `${rest} min`;
  return negative ? `-${text}` : text;
}

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '–';
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value == null) return '–';
  return value.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Prozentwert (bereits als 0–100 erwartet, nicht als Bruch) mit 2 Nachkommastellen. */
export function formatPercent(value: number | null | undefined): string {
  if (value == null) return '–';
  return `${formatNumber(value, 2)} %`;
}

export function initials(firstName?: string | null, lastName?: string | null): string {
  return `${(firstName ?? '').charAt(0)}${(lastName ?? '').charAt(0)}`.toUpperCase() || '?';
}

/** Deterministische Avatar-Farbe aus einem Namen. */
export function avatarColor(seed: string): string {
  const palette = ['#2563eb', '#7c3aed', '#db2777', '#059669', '#d97706', '#0891b2', '#dc2626', '#4f46e5'];
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return palette[hash % palette.length];
}

export const SLA_LABELS: Record<string, string> = {
  ok: 'Im Plan',
  warning: 'Frist läuft ab',
  overdue: 'Überfällig',
  met: 'SLA erfüllt',
  breached: 'SLA verletzt',
  none: 'Keine Frist',
};

export const SLA_VARIANTS: Record<string, string> = {
  ok: 'success',
  warning: 'warning',
  overdue: 'danger',
  met: 'success',
  breached: 'danger',
  none: '',
};

export const STATUS_VARIANTS: Record<string, string> = {
  new: 'info',
  triaged: 'info',
  assigned: 'primary',
  accepted: 'primary',
  in_progress: 'warning',
  waiting_material: 'warning',
  waiting_feedback: 'warning',
  done: 'success',
  closed: 'success',
  rejected: 'danger',
  cancelled: '',
  duplicate: '',
};

export const TIME_KIND_LABELS: Record<string, string> = {
  work: 'Arbeitszeit',
  travel: 'Fahrzeit',
  break: 'Pause',
  general: 'Allgemein',
};

export const ORDER_STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  pending_approval: 'Wartet auf Freigabe',
  approved: 'Freigegeben',
  ordered: 'Bestellt',
  partially_received: 'Teilweise geliefert',
  received: 'Geliefert',
  cancelled: 'Storniert',
};
